import { useMemo, useState } from 'react';
import { EmptyState } from '@/components/ui';
import { SegmentedControl, type SegmentedControlOption } from '@/components/ui/SegmentedControl';
import { cx } from '@/lib/cx';
import { pluralise } from '@/lib/format';
import { Money } from '@/components/ui/Money';
import { achievedTone, formatPct, formatUsd, sumUsd, type SalesTargetTone } from '@/lib/sales-targets';
import type { SalesTargetsView } from '@/lib/types';

/**
 * Per-project progress (Management → Sales targets): one compact bar per
 * project — achieved against target revenue, with a tick at expected-to-date —
 * plus a "Most behind" list of the five furthest behind pace.
 *
 * ── WHAT A ROW IS ───────────────────────────────────────────────────────────
 * A project that belongs to a pool is NOT drawn on its own: the pool carries
 * the plan and target (members' own plan/target are excluded from totals, so a
 * member bar would measure against a figure nobody is chasing). The pool row
 * shows the pool's achieved, which the server already sums over its members.
 * A row with no target revenue (`null`) cannot be measured and is left out,
 * counted on one line instead.
 *
 * ── MONEY ───────────────────────────────────────────────────────────────────
 * Every figure is the server's USD decimal string. Nothing here sums or
 * converts money. The only arithmetic is geometry and ordering: ratios and
 * comparisons of decimal strings taken in BigInt, reduced to basis points for
 * a CSS width. Expected-to-date is `paceGapUsd + achievedUsd`, an exact BigInt
 * addition of two server figures; it is never displayed as a number of its
 * own, only placed as the tick.
 *
 * ── "BEHIND" ────────────────────────────────────────────────────────────────
 * "x USD behind" (house rule: ISO code, never a symbol) is the PACE gap
 * (expected-to-date − achieved). Tone is the shared `achievedTone`. Before a cycle
 * starts, expected is 0 and nothing is behind; that is the honest reading,
 * since a target for a year that has not begun is not yet missed.
 */

type SortKey = 'behind' | 'target' | 'name';

const SORT_OPTIONS: readonly SegmentedControlOption<SortKey>[] = [
  { value: 'behind', label: 'Most behind' },
  { value: 'target', label: 'Largest target' },
  { value: 'name', label: 'A–Z' },
];

const MOST_BEHIND_COUNT = 5;

type Tone = SalesTargetTone;

const FILL: Record<Tone, string> = {
  success: 'bg-success',
  neutral: 'bg-fg-secondary',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

const TEXT: Record<Tone, string> = {
  success: 'text-success',
  neutral: 'text-fg-secondary',
  warning: 'text-warning',
  danger: 'text-danger',
};

/* ── exact decimal helpers (cents, BigInt) ─────────────────────────────────── */

function cents(value: string | null | undefined): bigint | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  const negative = trimmed.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? trimmed.slice(1) : trimmed).split('.');
  const two = `${fraction}00`.slice(0, 2);
  let units = BigInt(whole) * 100n + BigInt(two);
  // Round half away from zero on the third decimal.
  if (fraction.length > 2 && Number(fraction[2]) >= 5) units += 1n;
  return negative ? -units : units;
}

/** `part / whole` in basis points (0–10000), clamped; 0 when not drawable. */
function basisPoints(part: bigint | null, whole: bigint): number {
  if (part === null || whole <= 0n || part <= 0n) return 0;
  const bp = (part * 10000n) / whole;
  return bp > 10000n ? 10000 : Number(bp);
}

function compareBig(a: bigint | null, b: bigint | null): number {
  // Nulls sort last whatever the direction.
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a > b ? -1 : a < b ? 1 : 0;
}

/* ── rows ──────────────────────────────────────────────────────────────────── */

interface ProgressRow {
  key: string;
  name: string;
  /** Small second word: product family, or "Pool · N projects". */
  context: string | null;
  target: bigint;
  achieved: bigint | null;
  expected: bigint | null;
  gap: bigint | null;
  paceGap: bigint | null;
  /** The server's strings, for display and the shared tone rule. */
  source: RowSource;
  tone: Tone;
}

interface RowSource {
  targetRevenueUsd: string | null;
  achievedUsd: string | null;
  gapUsd: string | null;
  paceGapUsd: string | null;
  achievedPct: string | null;
}

function toRow(
  input: { key: string; name: string; context: string | null },
  source: RowSource,
): ProgressRow | null {
  const target = cents(source.targetRevenueUsd);
  if (target === null) return null;
  const achieved = cents(source.achievedUsd);
  const paceGap = cents(source.paceGapUsd);
  const expected = paceGap !== null && achieved !== null ? paceGap + achieved : null;
  return {
    key: input.key,
    name: input.name,
    context: input.context,
    target,
    achieved,
    expected,
    gap: cents(source.gapUsd),
    paceGap,
    source,
    tone: achievedTone(source),
  };
}

function pick(row: RowSource): RowSource {
  return {
    targetRevenueUsd: row.targetRevenueUsd,
    achievedUsd: row.achievedUsd,
    gapUsd: row.gapUsd,
    paceGapUsd: row.paceGapUsd,
    achievedPct: row.achievedPct,
  };
}

function buildRows(view: SalesTargetsView): { rows: ProgressRow[]; withoutTarget: number } {
  const poolIds = new Set(view.pools.map((pool) => pool.id));
  const pooledProjects = new Set(view.pools.flatMap((pool) => pool.projectIds));
  const rows: ProgressRow[] = [];
  let withoutTarget = 0;

  for (const pool of view.pools) {
    const row = toRow(
      {
        key: `pool:${pool.id}`,
        name: pool.name,
        context: `Pool · ${pluralise(pool.projectIds.length, 'project')}`,
      },
      pick(pool),
    );
    if (row) rows.push(row);
    else withoutTarget += 1;
  }

  for (const line of view.lines) {
    const pooled =
      (line.poolId !== null && poolIds.has(line.poolId)) || pooledProjects.has(line.projectId);
    if (pooled) continue;
    const row = toRow(
      { key: `project:${line.projectId}`, name: line.projectName, context: line.productName },
      pick(line),
    );
    if (row) rows.push(row);
    else withoutTarget += 1;
  }

  return { rows, withoutTarget };
}

function isBehind(row: ProgressRow): boolean {
  return row.paceGap !== null && row.paceGap > 0n;
}

function statusText(row: ProgressRow): string {
  if (isBehind(row)) return `${formatUsd(row.source.paceGapUsd, { whole: true })} behind`;
  return 'On track';
}

function ariaLabel(row: ProgressRow): string {
  const parts = [
    row.name,
    `achieved ${row.achieved === null ? 'not available' : formatUsd(row.source.achievedUsd, { whole: true })}`,
    `of target ${formatUsd(row.source.targetRevenueUsd, { whole: true })}`,
    formatPct(row.source.achievedPct),
  ];
  if (row.expected !== null && row.expected > 0n) {
    const expected = row.source.achievedUsd !== null && row.source.paceGapUsd !== null
      ? sumUsd([row.source.achievedUsd, row.source.paceGapUsd])
      : null;
    parts.push(`expected to date ${formatUsd(expected, { whole: true })}`);
  }
  parts.push(statusText(row));
  return parts.join(', ');
}

function sortRows(rows: ProgressRow[], sort: SortKey): ProgressRow[] {
  const copy = [...rows];
  const byName = (a: ProgressRow, b: ProgressRow) =>
    a.name.localeCompare(b.name, 'en', { sensitivity: 'base', numeric: true });
  if (sort === 'name') return copy.sort(byName);
  if (sort === 'target') {
    return copy.sort((a, b) => compareBig(a.target, b.target) || byName(a, b));
  }
  return copy.sort(
    (a, b) => compareBig(a.paceGap, b.paceGap) || compareBig(a.gap, b.gap) || byName(a, b),
  );
}

/* ── pieces ────────────────────────────────────────────────────────────────── */

function ProgressBar({ row }: { row: ProgressRow }) {
  const tone = row.tone;
  const achieved = row.achieved !== null && row.achieved > 0n ? row.achieved : 0n;
  // Scale to the larger of target and achieved, so over-achievement stays visible.
  const whole = achieved > row.target ? achieved : row.target;
  const fill = basisPoints(achieved, whole);
  const targetAt = basisPoints(row.target, whole);
  const expectedAt = row.expected !== null && row.expected > 0n ? basisPoints(row.expected, whole) : null;

  return (
    <div
      role="img"
      aria-label={ariaLabel(row)}
      className="relative h-2.5 w-full rounded-full bg-surface-sunken"
    >
      <div
        aria-hidden="true"
        className={cx('absolute inset-y-0 left-0 rounded-full', FILL[tone])}
        style={{ width: `${fill / 100}%` }}
      />
      {targetAt < 10000 ? (
        <div
          aria-hidden="true"
          className="absolute -inset-y-0.5 w-px bg-fg"
          style={{ left: `${targetAt / 100}%` }}
        />
      ) : null}
      {expectedAt !== null ? (
        <div
          aria-hidden="true"
          className="absolute -inset-y-1 w-0.5 -translate-x-1/2 rounded-full bg-fg-muted"
          style={{ left: `${expectedAt / 100}%` }}
        />
      ) : null}
    </div>
  );
}

function ProgressRowView({ row }: { row: ProgressRow }) {
  const tone = row.tone;
  return (
    <li className="flex min-w-0 flex-col gap-1.5 py-2.5">
      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <div className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-fg" title={row.name}>
            {row.name}
          </span>
          {row.context ? (
            <span className="block truncate text-xs text-fg-muted" title={row.context}>
              {row.context}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-baseline gap-2 text-xs tabular-nums">
          <span className={cx('font-medium', TEXT[tone])}>{formatPct(row.source.achievedPct)}</span>
          <span className={isBehind(row) ? TEXT[tone] : 'text-fg-muted'}>{statusText(row)}</span>
        </div>
      </div>
      <ProgressBar row={row} />
      <div className="flex flex-wrap justify-between gap-x-3 text-xs tabular-nums text-fg-muted">
        <Money amount={row.source.achievedUsd} currency="USD" />
        <Money amount={row.source.targetRevenueUsd} currency="USD" />
      </div>
    </li>
  );
}

function MostBehindList({ rows }: { rows: ProgressRow[] }) {
  if (rows.length === 0) return null;
  return (
    <section aria-labelledby="sales-targets-most-behind" className="rounded-md border border-border">
      <h4
        id="sales-targets-most-behind"
        className="border-b border-border px-3 py-2 text-xs font-medium uppercase tracking-wide text-fg-muted"
      >
        Most behind
      </h4>
      <ol className="divide-y divide-border">
        {rows.map((row, index) => (
          <li key={row.key} className="flex min-w-0 items-baseline gap-2 px-3 py-2 text-sm">
            <span className="w-4 shrink-0 text-xs tabular-nums text-fg-muted">{index + 1}</span>
            <span className="min-w-0 flex-1 truncate text-fg" title={row.name}>
              {row.name}
            </span>
            <span className="shrink-0 text-xs tabular-nums text-fg-muted">
              {formatPct(row.source.achievedPct)}
            </span>
            <span className={cx('shrink-0 text-xs font-medium tabular-nums', TEXT[row.tone])}>
              {statusText(row)}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ── component ─────────────────────────────────────────────────────────────── */

export function ProjectProgressChart({ view }: { view: SalesTargetsView }) {
  const [sort, setSort] = useState<SortKey>('behind');
  const { rows, withoutTarget } = useMemo(() => buildRows(view), [view]);
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
  const mostBehind = useMemo(
    () => sortRows(rows.filter(isBehind), 'behind').slice(0, MOST_BEHIND_COUNT),
    [rows],
  );

  return (
    <section aria-labelledby="sales-targets-project-progress" className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="sales-targets-project-progress" className="text-sm font-semibold text-fg">
          Project progress
        </h3>
        {rows.length > 1 ? (
          <div className="w-full sm:w-auto">
            <SegmentedControl label="Sort projects" value={sort} options={SORT_OPTIONS} onChange={setSort} />
          </div>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No project targets"
          description="Set target counts or revenue on the projects of this cycle to track progress here."
        />
      ) : (
        <>
          <MostBehindList rows={mostBehind} />
          <ul className="divide-y divide-border">
            {sorted.map((row) => (
              <ProgressRowView key={row.key} row={row} />
            ))}
          </ul>
        </>
      )}

      {withoutTarget > 0 ? (
        <p className="text-xs text-fg-muted">{pluralise(withoutTarget, 'project')} without a target</p>
      ) : null}
    </section>
  );
}
